import { Link, NavLink, useNavigate } from "react-router-dom";
import { ArrowUpRight, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEventCart } from "@/context/EventCartContext";
import { useT } from "@/context/I18nContext";
import LanguageSelector from "@/components/LanguageSelector";
import SectionsMenu from "@/components/SectionsMenu";
import MobileMenu from "@/components/MobileMenu";

const linkBase =
  "text-sm text-[#F7F4EE]/80 hover:text-[#E5A93C] transition-colors px-1 py-2 whitespace-nowrap";
const linkActive = "text-[#E5A93C] border-b border-[#E5A93C]/60";

export default function Nav() {
  const { user, logout } = useAuth();
  const { count } = useEventCart();
  const { t } = useT();
  const navigate = useNavigate();

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-[#0a0908]/80 border-b av-hairline"
      data-testid="app-nav"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 px-4 sm:px-6 lg:px-8 py-3">
        {/* LEFT: ArtVision + NMA logo + Sezioni menu + main nav */}
        <div className="flex items-center gap-4 min-w-0">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-home-link">
            <span className="text-[#E5A93C] text-lg">●</span>
            <span className="font-serif-display italic text-xl text-[#F7F4EE]">
              ArtVision
            </span>
          </Link>
          <Link to="/" aria-label="NMA · Napolitano Max Agency" className="shrink-0" data-testid="nma-top-logo">
            <img
              src="/nma-logo.png?v=2"
              alt="NMA · Napolitano Max Agency"
              className="h-12 w-12 object-contain drop-shadow-[0_4px_14px_rgba(229,169,60,0.35)]"
            />
          </Link>
          <SectionsMenu />
          <nav className="hidden lg:flex items-center gap-5">
            <NavLink
              to="/"
              end
              className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}
              data-testid="nav-vetrina"
            >
              {t("nav.vetrina")}
            </NavLink>
            <NavLink
              to="/artisti"
              className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}
              data-testid="nav-artisti"
            >
              {t("nav.artisti")}
            </NavLink>
            <NavLink
              to="/spettacoli"
              className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}
              data-testid="nav-spettacoli"
            >
              {t("nav.spettacoli")}
            </NavLink>
            <NavLink
              to="/servizi"
              className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}
              data-testid="nav-servizi"
            >
              {t("nav.servizi")}
            </NavLink>
            <NavLink
              to="/candidati"
              className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}
              data-testid="nav-candidati"
            >
              {t("nav.candidati")}
            </NavLink>
            <NavLink
              to="/chi-siamo"
              className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}
              data-testid="nav-chisiamo"
            >
              {t("nav.chisiamo")}
            </NavLink>
            <NavLink
              to="/richiedi"
              className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}
              data-testid="nav-richiedi"
            >
              {t("nav.richiedi")}
              {count > 0 && (
                <span
                  className="ml-2 text-[10px] bg-[#E5A93C] text-[#1a140a] rounded-full px-1.5 py-0.5 font-bold"
                  data-testid="cart-count"
                >
                  {count}
                </span>
              )}
            </NavLink>
          </nav>
        </div>

        {/* RIGHT: auth actions + language + mobile hamburger */}
        <div className="flex items-center justify-end gap-2 sm:gap-3 shrink-0">
          <div className="hidden sm:block">
            <LanguageSelector />
          </div>
          {user && user.role ? (
            <>
              {user.role === "client" && (
                <Link
                  to="/le-mie-richieste"
                  className="hidden lg:inline-flex av-pill av-pill-ghost text-sm"
                  data-testid="nav-my-requests"
                >
                  {t("nav.mierichieste")}
                </Link>
              )}
              {user.role === "admin" && (
                <Link
                  to="/admin"
                  className="hidden lg:inline-flex av-pill av-pill-ghost text-sm"
                  data-testid="nav-admin"
                >
                  {t("nav.areagestione")} <ArrowUpRight size={14} />
                </Link>
              )}
              <button
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
                className="hidden lg:inline-flex av-pill av-pill-ghost text-sm"
                data-testid="logout-btn"
                title={user.email}
              >
                <LogOut size={14} /> {t("nav.esci")}
              </button>
            </>
          ) : (
            <Link
              to="/login"
              className="hidden lg:inline-flex av-pill av-pill-ghost text-sm"
              data-testid="nav-login"
            >
              {t("nav.areagestione")} <ArrowUpRight size={14} />
            </Link>
          )}
          <MobileMenu />
        </div>
      </div>
    </header>
  );
}
